import { useState } from 'react';
import { useMutation } from "@apollo/client/react";

import Form from 'web/form/Form';
import {
	Dialog,
	DialogTitle,
	DialogActions,
	DialogContent,
	Button,
} from 'components/dialogs/DialogBox';

import { UPDATE_OWN_USER } from 'api/mutations/userMutations';

export default function UserProfileForm({
	user,
	onSuccess,
	onCancel,
	onError,
	refetchQueries,
	children,
}) {
	const [verifyResolve, setVerifyResolve] = useState(null);
	const [verifyMessage, setVerifyMessage] = useState(null);

	const verify = async message => {
		setVerifyMessage(message);

		return new Promise(resolve => {
			setVerifyResolve(() => resolve);
		});
	};

	const handleConfirm = confirmed => {
		setVerifyMessage('');
		verifyResolve(confirmed);
	};

	const [updateOwnUser] = useMutation(UPDATE_OWN_USER, {
		refetchQueries,
		fetchPolicy: 'no-cache',
	});

	const onSubmit = async values => {
		try {
			onError && onError(null);

			const verifyFields = [];

			if (values.email !== user.email) {
				verifyFields.push('e-post');
			}

			if (values.phone !== user.phone) {
				verifyFields.push('telefon');
			}

			if (verifyFields.length > 0) {
				const verified = await verify(
					`Du har endret felt som trenger verifisering: ${verifyFields.join(
						', '
					)}. Er du sikker på at du vil lagre og gjennomføre verifiseringen?`
				);

				if (!verified) {
					onCancel && onCancel();

					return;
				}
			}

			values.address = {
				street_address: values.street_address || undefined,
				postal_code: values.postal_code || undefined,
				locality: values.locality || undefined,
				country: values.country || undefined,
			};

			if (
				values.isPoliticallyExposedPerson &&
				values.userPoliticallyExposedPersonRoles.length === 0
			) {
				onError &&
					onError('Mangler relasjoner for politisk eksponert person');

				return;
			}

			if (!values.isPoliticallyExposedPerson) {
				values.userPoliticallyExposedPersonRoles = [];
			}

			const { data } = await updateOwnUser({
				variables: {
					_id: user._id,
					forceUserVerificationDate: null,
					userLastVerificationDate: new Date(),
					...values,
				},
			});

			if (data.user) {
				onSuccess && onSuccess();
			} else {
				onError && onError('Kunne ikke oppdatere bruker');
			}
		} catch (error) {
			console.error(error);

			onError && onError(error);
		}
	};

	if (!user) {
		console.error('User is required for UserProfileForm');
		return null;
	}

	const values = {
		name: user.name,
		email: user.email,
		phone: user.phone,
		street_address: user.address?.street_address ?? null,
		postal_code: user.address?.postal_code ?? null,
		locality: user.address?.locality ?? null,
		country: user.address?.country ?? null,
		citizenship: user.citizenship,
		isPoliticallyExposedPerson: user.isPoliticallyExposedPerson,
		userPoliticallyExposedPersonRoles:
			user?.userPoliticallyExposedPersonRoles?.map(role => ({
				type: role.type,
				name: role.name,
				address: role.address,
			})) ?? [],
	};

	return (
		<>
			<Form values={values} onSubmit={onSubmit}>
				{children}
			</Form>
			{verifyMessage && (
				<ConfirmDialog
					message={verifyMessage}
					onClose={() => handleConfirm(false)}
					onConfirm={() => handleConfirm(true)}
				/>
			)}
		</>
	);
}

// Can not use useVerify because this form can be used outside of the verify context
function ConfirmDialog({ message, onConfirm, onClose }) {
	return (
		<Dialog
			open="true"
			onClose={onClose}
			aria-labelledby="confirm-dialog-title"
			aria-describedby="confirm-dialog-title"
		>
			<DialogTitle id="confirm-dialog-title">
				Bekreft endringer
			</DialogTitle>

			{message && (
				<DialogContent id="confirm-dialog-title">
					{message}
				</DialogContent>
			)}

			<DialogActions>
				<Button onClick={onClose}>Nei</Button>
				<Button onClick={onConfirm}>Ja</Button>
			</DialogActions>
		</Dialog>
	);
}
